/** @format */

import Vue from 'vue';
import VueI18n from 'vue-i18n';
import messages from '../locales/locale-message-loaders/en';
import {$csLocaleConfig} from '../locales/config';
import {Day} from '@/lc-datepicker/src/lc-datepicker/libs/bootstrap';
import $cs_ from '@/js/plugins/cs-underscore-helpers';

let csConfigLocales = [];
let localeNumberCodes = {}; //used with Intl.NumberFormat to do number translations
let localeValidationCodes = {}; //References the json file in vee-validate/dist/locale
let googleTranslateCodes = {}; //https://cloud.google.com/translate/docs/languages
let localeAmChartCodes = {}; //https://github.com/amcharts/amcharts4/tree/master/src/lang
let datepickerLocaleCodes = {}; //https://github.com/iamkun/dayjs/tree/dev/src/locale
let syncFusionLocaleCodes = {}; //in node_modules/cldr-data/main/* directory
let dayJsByLocale = {};
let localeAmChartCache = {};

// take the config and split it into useful module properties
for (let lang of Object.keys($csLocaleConfig)) {
	const localeObj = $csLocaleConfig[lang];
	localeNumberCodes[lang] = localeObj.numberFormatCode;
	localeValidationCodes[lang] = localeObj.veeValidateLocaleCode;
	localeAmChartCodes[lang] = localeObj.amChartLocaleCode;
	datepickerLocaleCodes[lang] = localeObj.datepickerLocaleCode;
	syncFusionLocaleCodes[lang] = localeObj.syncFusionLocaleCode;
	googleTranslateCodes[lang] = localeObj.googleTranslateCode;
}

Vue.use(VueI18n);

//this is used as our main global i18n instance
const csI18nPriv = new VueI18n({
	locale: 'en',
	fallbackLocale: 'en',
	messages: {en: messages.messages}
});

const loadedLanguages = ['en']; // our default language that is preloaded
const _isMultiLingual = () => $cs_.getAppNode()?.$isMultiLingual();

/**
 * @public
 * @description
 * Update the module locale to the incoming language.
 * 
 * @param {string} lang the shorthand version of the language we are switching to
 * @returns 
 */
function setI18nLanguage(lang) {
	csI18nPriv.locale = lang;
	return lang;
}

/**
 * @public
 * @description
 * Update the module locale to the incoming language.
 * 
 * @param {string} lang the shorthand version of the language we are switching to
 * @returns 
 */
const setCSLocaleLanguage = async (lang) => {
	// If the same language
	if (csI18nPriv.locale === lang) {
		return Promise.resolve(setI18nLanguage(lang));
	}

	// If the language was already loaded
	if (loadedLanguages.includes(lang)) {
		return Promise.resolve(setI18nLanguage(lang));
	}

	const data = await import(/* webpackChunkName: "lang-[request]" */ `./../locales/locale-message-loaders/${lang}.js`);
	csI18nPriv.setLocaleMessage(lang, data.default.messages);
	loadedLanguages.push(lang);

	return setI18nLanguage(lang);
}
export {setCSLocaleLanguage};

/********CHARTS**********/
/**
 * @public
 * @description
 * Loads locale data for use with AM Charts either from cache or directly from import.
 * 
 * @returns {Promise}
 */
const loadChartLocale = async () => {
	if (!localeAmChartCodes[csI18nPriv.locale]) {
		return Promise.reject(`${csI18nPriv.locale} does not have a localeAmChartCodes code set`);
	}

	// If the language was already loaded
	if (localeAmChartCache[csI18nPriv.locale]) {
		return Promise.resolve(localeAmChartCache[csI18nPriv.locale]);
	}

	const data = import(/* webpackChunkName: "amchart-lang-[request]" */ `@amcharts/amcharts4/lang/${localeAmChartCodes[csI18nPriv.locale]}.js`);
	localeAmChartCache[csI18nPriv.locale] = data.default;
	return data;
}
export {loadChartLocale};

/**
 * @public
 * @description
 * Loads locale data for use with Syncfusion (currently just Gantt charts) directly from import.
 * 
 * @returns {Promise}
 */
const loadSyncFusionLocale = async () => {
	const syncFusionLocaleDir = syncFusionLocaleCodes[csI18nPriv.locale];
	let files = [];
	// let gregorian = await import(/* webpackChunkName: "syncfusion-lang-[request]" */`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/ca-gregorian.json`);
	// files.push(gregorian.default)

	// let numbers = await import(/* webpackChunkName: "syncfusion-lang-[request]" */`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/numbers.json`);
	// files.push(numbers.default)

	// let timeZoneNames = await import(/* webpackChunkName: "syncfusion-lang-[request]" */`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/timeZoneNames.json`);
	// files.push(timeZoneNames.default)

	// let currencies = await import(/* webpackChunkName: "syncfusion-lang-[request]" */`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/currencies.json`);
	// files.push(currencies.default)

	// let dateFields = await import(/* webpackChunkName: "syncfusion-lang-[request]" */`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/dateFields.json`);
	// files.push(dateFields.default)

	//IF WE BEGIN TO GET MEMORY ISSUES AGAIN - REMOVE BELOW IN FAVOUR OF THE ABOVE
	return Promise.all([
		import(`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/ca-gregorian.json`),
		import(`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/numbers.json`),
		import(`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/timeZoneNames.json`),
		import(`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/currencies.json`),
		import(`./../../../node_modules/cldr-data/main/${syncFusionLocaleDir}/dateFields.json`),
	])
	.then(([gregorian, numbers, timeZoneNames, currencies, dateFields]) => {
		files.push(gregorian.default);
		files.push(numbers.default);
		files.push(timeZoneNames.default);
		files.push(currencies.default);
		files.push(dateFields.default);
		return files;
	});
}

export {loadSyncFusionLocale};

/********DAY JS**********/
/**
 * @public
 * @description
 * get localised data from dayjs
 * 
 * @returns {Promise}
 */
const loadDayJsLocale = async () => {
	const localSettings = await import(/* webpackChunkName: "locale" */ `dayjs/locale/${privDatepickerLocaleCode()}.js`)
	return localSettings.default;
}
export {loadDayJsLocale};

/**
 * @description
 * Have an instance of dayjs available which is assigned to the given locale
 *
 * @param {String} locale
 *
 * @returns {Promise} -
 */
const loadDayJsObjectByLocale = async (locale) => {
	const {dayjs, localSettings} = await Day.getLocalDayJsObject(locale);
	dayJsByLocale = dayjs;
	return Promise.resolve({dayjs, localSettings});
}
export {loadDayJsObjectByLocale};

/********HELPERS**********/
/**
 * @description
 * Called from our Vue helper method this.$dayJsByLocale(),
 * this gives us a dayjs instance for use in the app (externally to the datepicker)
 * that is tied to the current locale. We assigned this in cs-app when we call `setUserLocale() -> loadDayJsObjectByLocale() above`
 */
export function getDayJsByLocale() {
	return dayJsByLocale;
}

/**
 * @public
 * @description
 * Return the number code for the current locale
 * 
 * @returns {String}
 */
export function numberFormatCode() {
	if (!localeNumberCodes[csI18nPriv.locale]) {
		console.error(`${csI18nPriv.locale} does not have a numberFormatCode set`);
	}
	return localeNumberCodes[csI18nPriv.locale];
}

/**
 * @public
 * @description
 * Return the vee validate code for the current locale
 * 
 * @returns {String}
 */
export function validationFileLocaleCode() {
	if (!localeValidationCodes[csI18nPriv.locale]) {
		console.error(`${csI18nPriv.locale} does not have a localeValidationCodes code set`);
	}
	return localeValidationCodes[csI18nPriv.locale];
}

/**
 * @public
 * @description
 * Return the syncfusion locale code for the current locale
 * 
 * @returns {String}
 */
export function syncfusionLocaleCode() {
	if (!syncFusionLocaleCodes[csI18nPriv.locale]) {
		console.error(`${csI18nPriv.locale} does not have a syncFusionLocaleCodes code set`);
	}
	return syncFusionLocaleCodes[csI18nPriv.locale];
}

/*
Written seperately to allow use within this module as well as from outside
*/
function privDatepickerLocaleCode() {
	if (!datepickerLocaleCodes[csI18nPriv.locale]) {
		console.error(`${csI18nPriv.locale} does not have a datepickerLocaleCodes code set`);
	}
	return datepickerLocaleCodes[csI18nPriv.locale];
}

/**
 * @public
 * @description
 * Return the datepicker code for the current locale
 * 
 * @returns {String}
 */
export function datepickerLocaleCode() {
	return privDatepickerLocaleCode();
}

/*
CONFIG STATIC TRANSLATIONS
*/
/**
 * @description
 * Import CS static localised translation files
 *
 * @param {String} lang
 *
 * @returns {Object} -
 */
const importLocaleData = async (lang) => {
	const data = import(/* webpackChunkName: "lang-[request]" */ `./../locales/locale-message-loaders/${lang}.js`);
	return data.default;
}
export {importLocaleData};

/**
 * @public
 * @description
 * Sets an array of the application locales, mapped into their two digit code
 * 
 * @param {Array} locales 
 * @returns {Array}
 */
export function setCsConfigLocales(locales) {
	csConfigLocales = locales.map((l) => l.twoDigitCode);
	return csConfigLocales;
}

/**
 * @public
 * @description
 * Gets the array of application locales, mapped as above
 * 
 * @returns {Array}
 */
export function getCsConfigLocales() {
	return csConfigLocales;
}

export {csI18nPriv as $csI18n};


//PUBLIC TRANSLATION METHODS
const $translate = {};

/**
 * translate with Arge Module version
 * USE THIS FOR ALL TRANSLATION IN THE FUTURE NOT ON this
 * @param {*} translationPath 
 * @param {*} args 
 * @returns 
 */
$translate.withArgs = function(translationPath, args) {           
	if (csI18nPriv.te(translationPath)) {
		return csI18nPriv.t(translationPath, args);
	} else {
		console.error(`${translationPath} not found in locale messages (WITH ARGS)`)
	}          
	return '';
}

/**
 * @public
 * @description
 * Translates an item if multilingual is on, or returns the default version if not
 * USE THIS FOR ALL TRANSLATION IN THE FUTURE NOT ON this
 *
 * @param {String} translationPath The path to the translation key
 * @param {String} defaultValue The value to return if multilingual is off
 *
 * @returns {String}
 */
$translate.withDefault = function(translationPath, defaultValue) { 
	if (_isMultiLingual() === false && defaultValue) return defaultValue;
	let result = defaultValue;
	
	if(csI18nPriv.te(translationPath)) {
		result = csI18nPriv.t(translationPath);
	} else {
		if (!defaultValue) {
			// using to help debug during the dev process
			//console.warn(`${translationPath} not found in global locale messages, using ${defaultValue} as the default value`);
		} else {
			//console.warn(`${translationPath} -> ${defaultValue} as the default value`);
		}
	}
	return result;
}

export {$translate};
